import React from "react";
import { motion } from "framer-motion";

// Feedback loop mini-infographic used inside the dark result section.
// Placed between the metric cards and the result headline.

const RED_BORDER = "rgba(184,50,50,0.25)";
const RED_SOFT_BG = "rgba(184,50,50,0.08)";
const RED_STEP_BG = "rgba(184,50,50,0.12)";
const RED_STEP_BORDER = "rgba(184,50,50,0.2)";
const RED_STEP_COLOR = "#FCA5A5";
const RED_LABEL = "#F87171";

const GREEN_BORDER = "rgba(30,123,75,0.25)";
const GREEN_SOFT_BG = "rgba(30,123,75,0.08)";
const GREEN_STEP_BG = "rgba(30,123,75,0.12)";
const GREEN_STEP_BORDER = "rgba(30,123,75,0.2)";
const GREEN_STEP_COLOR = "#86EFAC";

const NEUTRAL_STEP = {
  backgroundColor: "rgba(255,255,255,0.05)",
  color: "var(--rtd-on-dark-soft)",
  border: "1px solid rgba(255,255,255,0.08)",
};

const stepBase = {
  borderRadius: 6,
  padding: "7px 6px",
  fontSize: 11,
  fontWeight: 500,
  textAlign: "center",
  lineHeight: 1.3,
};

function Step({ children, tone = "red" }) {
  const style =
    tone === "neutral"
      ? { ...stepBase, ...NEUTRAL_STEP }
      : tone === "green"
        ? {
            ...stepBase,
            backgroundColor: GREEN_STEP_BG,
            color: GREEN_STEP_COLOR,
            border: `1px solid ${GREEN_STEP_BORDER}`,
          }
        : {
            ...stepBase,
            backgroundColor: RED_STEP_BG,
            color: RED_STEP_COLOR,
            border: `1px solid ${RED_STEP_BORDER}`,
          };
  return (
    <div className="font-body" style={style}>
      {children}
    </div>
  );
}

function Arrow({ tone = "red" }) {
  return (
    <div
      aria-hidden="true"
      style={{
        color: tone === "green" ? GREEN_STEP_COLOR : RED_LABEL,
        fontSize: 14,
        margin: "3px 0",
        textAlign: "center",
        lineHeight: 1,
      }}
    >
      ↓
    </div>
  );
}

export default function FeedbackLoop() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      data-testid="feedback-loop-infographic"
      style={{ maxWidth: 480, margin: "20px auto", width: "100%" }}
    >
      <div
        className="font-body"
        style={{
          fontSize: 11,
          color: "var(--rtd-on-dark-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          textAlign: "center",
          marginBottom: 16,
        }}
      >
        Where your signal is breaking
      </div>

      <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {/* LEFT — Right now */}
        <div
          data-testid="feedback-loop-left"
          style={{
            backgroundColor: RED_SOFT_BG,
            border: `1px solid ${RED_BORDER}`,
            borderRadius: 10,
            padding: "14px 12px",
          }}
        >
          <div
            className="font-body"
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: RED_LABEL,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            Right now
          </div>

          <Step tone="neutral">Ad runs</Step>
          <Arrow tone="red" />
          <Step tone="red">Form fill</Step>
          <Arrow tone="red" />
          <Step tone="red">Ghost lead</Step>
          <Arrow tone="red" />
          <div
            className="font-body"
            style={{
              backgroundColor: "rgba(184,50,50,0.15)",
              border: "1px solid rgba(184,50,50,0.3)",
              borderRadius: 4,
              padding: "4px 6px",
              fontSize: 10,
              color: RED_LABEL,
              fontWeight: 700,
              textAlign: "center",
              width: "100%",
            }}
          >
            ⚠ Loop breaks
          </div>
          <Arrow tone="red" />
          <Step tone="red">Meta learns from wrong people</Step>
        </div>

        {/* RIGHT — Fixed signal */}
        <div
          data-testid="feedback-loop-right"
          style={{
            backgroundColor: GREEN_SOFT_BG,
            border: `1px solid ${GREEN_BORDER}`,
            borderRadius: 10,
            padding: "14px 12px",
          }}
        >
          <div
            className="font-body"
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: GREEN_STEP_COLOR,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              textAlign: "center",
              marginBottom: 12,
            }}
          >
            Fixed signal
          </div>

          <Step tone="neutral">Ad runs</Step>
          <Arrow tone="green" />
          <Step tone="green">Form fill</Step>
          <Arrow tone="green" />
          <Step tone="green">CRM qualifies</Step>
          <Arrow tone="green" />
          <Step tone="green">Revenue fired back</Step>
          <Arrow tone="green" />
          <Step tone="green">Meta finds more buyers</Step>
        </div>
      </div>

      {/* Results row */}
      <div
        className="grid"
        style={{ gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 12 }}
      >
        <div
          style={{
            backgroundColor: "rgba(184,50,50,0.1)",
            border: `1px solid ${RED_BORDER}`,
            borderRadius: 8,
            padding: "10px 8px",
            textAlign: "center",
          }}
        >
          <div
            className="font-serif-display"
            style={{ fontSize: 22, color: RED_LABEL, lineHeight: 1 }}
          >
            4
          </div>
          <div
            className="font-body"
            style={{ fontSize: 10, color: "var(--rtd-on-dark-muted)", marginTop: 3 }}
          >
            paying patients per ₹1L
          </div>
        </div>
        <div
          style={{
            backgroundColor: "rgba(30,123,75,0.1)",
            border: `1px solid ${GREEN_BORDER}`,
            borderRadius: 8,
            padding: "10px 8px",
            textAlign: "center",
          }}
        >
          <div
            className="font-serif-display"
            style={{ fontSize: 22, color: GREEN_STEP_COLOR, lineHeight: 1 }}
          >
            20
          </div>
          <div
            className="font-body"
            style={{ fontSize: 10, color: "var(--rtd-on-dark-muted)", marginTop: 3 }}
          >
            paying patients per ₹1L
          </div>
        </div>
      </div>

      <div
        style={{
          height: 1,
          background: "rgba(255,255,255,0.07)",
          margin: "16px 0",
        }}
      />

      <div
        className="font-body"
        style={{
          fontSize: 11,
          color: "var(--rtd-on-dark-muted)",
          textAlign: "center",
          lineHeight: 1.5,
        }}
      >
        Same budget. Same front desk.{" "}
        <span style={{ color: "var(--rtd-gold)" }}>Different signal.</span>
      </div>
    </motion.div>
  );
}
